import { create } from 'zustand';
import { generateId } from '../utils/id';
import type { 
  MortgageTermsData, 
  Country, 
  PaymentFrequency 
} from '../types/mortgageTypes';
import type { ShareState } from '../utils/shareUtils';
import type { PresetTabConfig } from '../utils/presetMortgageTerms';
import { calculateSchedule, applyManualOneTimePaymentEdits } from '../utils/amortizationEngine';
import {
  getSyncDefaultCountryFromLocale,
  mortgageTermsForCountryGeoSwitch,
} from '../utils/geoDefaultCountry';

export interface AnnualExpensesData {
  propertyTaxes: {
    type: 'percent' | 'amount';
    value: number;
  };
  homeInsurance: {
    type: 'percent' | 'amount';
    value: number;
  };
  hoaUtilities: {
    type: 'percent' | 'amount';
    value: number;
  };
}

export interface ExtraPaymentsData {
  frequency: 'monthly' | 'semi-monthly' | 'bi-weekly' | 'weekly' | 'yearly';
  amount: number;
  startDate: Date | null;
  endDate?: Date | null;
}

export interface AmortizationEntry {
  date: Date;
  payment: number;
  principalPayment: number;
  interestPayment: number;
  expensePayment: number;
  totalPayment: number;
  remainingBalance: number;
  oneTimePayment?: number;
  pmiPayment?: number; // Conventional PMI (separate from interestPayment; cancels at 80% LTV)
  currentRate?: number; // ARM/variable rate for this period
  isRenewalDate?: boolean; // Canadian term renewal boundary
}

export interface CalculatorTabData {
  mortgageTerms: MortgageTermsData;
  annualExpenses: AnnualExpensesData;
  extraPayments: ExtraPaymentsData;
  amortizationSchedule: AmortizationEntry[];
  hasManualExtraPaymentEdits: boolean;
}

export const TAB_COLORS = ['#C9A84C', '#4A8FE7', '#2E7D6B', '#9B8AFB', '#E8C97A'];

export interface CalculatorTab {
  id: string;
  title: string;
  color: string;
  data: CalculatorTabData;
}

export interface CalculatorStore {
  calculatorTabs: CalculatorTab[];
  activeTabId: string;
  hasUnsavedChanges: boolean;
  /** When set, user navigated with `?s=` but chose to defer loading until confirming (unsaved edits or extra tabs). */
  pendingShareState: ShareState | null;
  renameTargetId: string | null;
  setPendingShareState: (state: ShareState | null) => void;
  addCalculatorTab: () => void;
  removeCalculatorTab: (tabId: string) => void;
  renameCalculatorTab: (tabId: string, newTitle: string) => void;
  setRenameTargetId: (tabId: string | null) => void;
  setActiveTabId: (tabId: string) => void;
  updateCalculatorTabData: (tabId: string, data: Partial<CalculatorTabData>) => void;
  applyPresetToAllTabs: (presetTabs: PresetTabConfig[]) => void;
  calculateAmortizationSchedule: (tabId: string) => void;
  setHasUnsavedChanges: (value: boolean) => void;
  loadSharedState: (shareState: ShareState) => void;
  applyInitialGeoCountryToAllTabs: (country: Country) => void;
}

// Re-export types for backward compatibility
export type { MortgageTermsData } from '../types/mortgageTypes';

const syncDefaultCountry = getSyncDefaultCountryFromLocale();

// Function to get default calculator data
const getDefaultCalculatorData = (): CalculatorTabData => ({
  mortgageTerms: {
    country: syncDefaultCountry,
    homePrice: 800000,
    downPaymentAmount: 160000,
    downPaymentPercentage: 20,
    mortgageType: 'fixed',
    loanProgram: 'conventional',
    rate: 4.5,
    amortizationPeriod: 30,
    startDate: new Date(),
    paymentFrequency: 'monthly',
    pmi: {
      type: 'percent',
      value: 0
    },
    canadianTermDetails:
      syncDefaultCountry === 'Canada'
        ? { termLength: 5, showRenewalScenario: false, renewalRate: undefined, renewalRates: undefined }
        : undefined,
  },
  annualExpenses: {
    propertyTaxes: {
      type: 'percent',
      value: 1.2
    },
    homeInsurance: {
      type: 'amount',
      value: 1200
    },
    hoaUtilities: {
      type: 'amount',
      value: 0
    }
  },
  extraPayments: {
    frequency: 'monthly',
    amount: 0,
    startDate: null
  },
  amortizationSchedule: [],
  hasManualExtraPaymentEdits: false
});

// Helper function to create independent tab data
const createIndependentTabData = (data: CalculatorTabData): CalculatorTabData => {
  return {
    mortgageTerms: {
      ...data.mortgageTerms,
      pmi: { ...data.mortgageTerms.pmi }
    },
    annualExpenses: {
      propertyTaxes: { ...data.annualExpenses.propertyTaxes },
      homeInsurance: { ...data.annualExpenses.homeInsurance },
      hoaUtilities: { ...data.annualExpenses.hoaUtilities }
    },
    extraPayments: { ...data.extraPayments },
    amortizationSchedule: data.amortizationSchedule.map(entry => ({ ...entry })),
    hasManualExtraPaymentEdits: data.hasManualExtraPaymentEdits
  };
};

const hasRequiredMortgageTerms = (terms?: MortgageTermsData): boolean => {
  if (!terms) return false;
  const startDate = new Date(terms.startDate);
  return (
    Number.isFinite(terms.homePrice) &&
    terms.homePrice > 0 &&
    Number.isFinite(terms.downPaymentAmount) &&
    terms.downPaymentAmount >= 0 &&
    Number.isFinite(terms.rate) &&
    terms.rate >= 0 &&
    Number.isFinite(terms.amortizationPeriod) &&
    terms.amortizationPeriod > 0 &&
    !isNaN(startDate.getTime()) &&
    Boolean(terms.paymentFrequency)
  );
};

export const useCalculatorStore = create<CalculatorStore>((set, get) => {
  // Generate initial tab IDs
  const initialTabId = generateId();
  const secondTabId = generateId();
  const defaultData = getDefaultCalculatorData();
  const secondDefaultData = {
    ...getDefaultCalculatorData(),
    mortgageTerms: {
      ...getDefaultCalculatorData().mortgageTerms,
      paymentFrequency: 'accelerated-bi-weekly' as PaymentFrequency
    }
  };

  // Pre-calculate both tabs so the comparison preview is accurate on first load
  const initialData = createIndependentTabData({
    ...defaultData,
    amortizationSchedule: calculateSchedule({
      mortgageTerms: defaultData.mortgageTerms,
      annualExpenses: defaultData.annualExpenses,
      extraPayments: defaultData.extraPayments,
    })
  });
  const secondTabData = createIndependentTabData({
    ...secondDefaultData,
    amortizationSchedule: calculateSchedule({
      mortgageTerms: secondDefaultData.mortgageTerms,
      annualExpenses: secondDefaultData.annualExpenses,
      extraPayments: secondDefaultData.extraPayments,
    })
  });
  
  return {
    calculatorTabs: [
      {
        id: initialTabId,
        title: 'CALC 1',
        color: TAB_COLORS[0],
        data: initialData
      },
      {
        id: secondTabId,
        title: 'CALC 2',
        color: TAB_COLORS[1],
        data: secondTabData
      }
    ],
    activeTabId: initialTabId,
    hasUnsavedChanges: false,
    pendingShareState: null,
    renameTargetId: null,

    setPendingShareState: (state) => {
      set({ pendingShareState: state });
    },

    addCalculatorTab: () => {
      const { calculatorTabs } = get();
      const newTabId = generateId();
      const usedNumbers = calculatorTabs
        .map(t => {
          const match = t.title.match(/^CALC (\d+)$/i);
          return match ? parseInt(match[1], 10) : 0;
        });
      const newTabNumber = Math.max(0, ...usedNumbers) + 1;
      
      // Create a new tab with completely independent data
      const newTabData = createIndependentTabData(getDefaultCalculatorData());
      
      set((state) => {
        const usedColors = new Set(state.calculatorTabs.map(t => t.color));
        const nextColor =
          TAB_COLORS.find(c => !usedColors.has(c)) ??
          TAB_COLORS[state.calculatorTabs.length % TAB_COLORS.length];

        return {
          calculatorTabs: [
            ...state.calculatorTabs,
            {
              id: newTabId,
              title: `CALC ${newTabNumber}`,
              color: nextColor,
              data: newTabData
            }
          ],
          activeTabId: newTabId
        };
      });
    },
    
    removeCalculatorTab: (tabId) => {
      const { calculatorTabs, activeTabId } = get();
      if (calculatorTabs.length <= 1) return;

      const remainingTabs = calculatorTabs.filter(tab => tab.id !== tabId);
      let newActiveTabId = activeTabId;

      if (activeTabId === tabId) {
        newActiveTabId = remainingTabs[0].id;
      } else if (activeTabId === 'compare' && remainingTabs.length < 2) {
        newActiveTabId = remainingTabs[0].id;
      }

      set({ calculatorTabs: remainingTabs, activeTabId: newActiveTabId });
    },

    renameCalculatorTab: (tabId, newTitle) => {
      set((state) => ({
        calculatorTabs: state.calculatorTabs.map(tab =>
          tab.id === tabId ? { ...tab, title: newTitle } : tab
        )
      }));
    },

    setRenameTargetId: (tabId) => {
      set({ renameTargetId: tabId });
    },

    setActiveTabId: (tabId) => {
      set({ activeTabId: tabId });
    },
    
    setHasUnsavedChanges: (value) => {
      set({ hasUnsavedChanges: value });
    },
    
    updateCalculatorTabData: (tabId, data) => {
      set((state) => {
        const updatedTabs = state.calculatorTabs.map(tab => {
          if (tab.id === tabId) {
            // Create a completely new data object for the tab
            const updatedData: CalculatorTabData = {
              mortgageTerms: data.mortgageTerms 
                ? { ...tab.data.mortgageTerms, ...data.mortgageTerms }
                : tab.data.mortgageTerms,
              annualExpenses: data.annualExpenses
                ? {
                    propertyTaxes: data.annualExpenses.propertyTaxes 
                      ? { ...tab.data.annualExpenses.propertyTaxes, ...data.annualExpenses.propertyTaxes }
                      : tab.data.annualExpenses.propertyTaxes,
                    homeInsurance: data.annualExpenses.homeInsurance
                      ? { ...tab.data.annualExpenses.homeInsurance, ...data.annualExpenses.homeInsurance }
                      : tab.data.annualExpenses.homeInsurance,
                    hoaUtilities: data.annualExpenses.hoaUtilities
                      ? { ...tab.data.annualExpenses.hoaUtilities, ...data.annualExpenses.hoaUtilities }
                      : tab.data.annualExpenses.hoaUtilities
                  }
                : tab.data.annualExpenses,
              extraPayments: data.extraPayments
                ? { ...tab.data.extraPayments, ...data.extraPayments }
                : tab.data.extraPayments,
              amortizationSchedule: data.amortizationSchedule || tab.data.amortizationSchedule,
              hasManualExtraPaymentEdits: data.hasManualExtraPaymentEdits !== undefined 
                ? data.hasManualExtraPaymentEdits 
                : tab.data.hasManualExtraPaymentEdits
            };
            
            return { ...tab, data: updatedData };
          }
          return tab;
        });
        
        return { 
          calculatorTabs: updatedTabs,
          hasUnsavedChanges: true 
        };
      });
    },
    
    applyPresetToAllTabs: (presetTabs) => {
      set((state) => {
        const presetTabsForIndex = (index: number): PresetTabConfig =>
          presetTabs[index] ??
          presetTabs[presetTabs.length - 1] ?? {
            title: `CALC ${index + 1}`,
            mortgageTerms: {},
          };

        const updatedPresetTabs: CalculatorTab[] = presetTabs.map((preset, index) => {
          const existingTab = state.calculatorTabs[index];
          if (existingTab) {
            return {
              ...existingTab,
              title: preset.title,
              data: {
                ...existingTab.data,
                mortgageTerms: { ...existingTab.data.mortgageTerms, ...preset.mortgageTerms },
                amortizationSchedule: [],
              },
            };
          }

          const defaultData = createIndependentTabData(getDefaultCalculatorData());
          return {
            id: generateId(),
            title: preset.title,
            color: TAB_COLORS[index % TAB_COLORS.length],
            data: {
              ...defaultData,
              mortgageTerms: { ...defaultData.mortgageTerms, ...preset.mortgageTerms },
              amortizationSchedule: [],
            },
          };
        });

        const extraTabs = state.calculatorTabs.slice(presetTabs.length).map((tab, offset) => {
          const index = presetTabs.length + offset;
          const preset = presetTabsForIndex(index);
          return {
            ...tab,
            title: preset.title,
            data: {
              ...tab.data,
              mortgageTerms: { ...tab.data.mortgageTerms, ...preset.mortgageTerms },
              amortizationSchedule: [],
            },
          };
        });

        return {
          calculatorTabs: [...updatedPresetTabs, ...extraTabs],
          hasUnsavedChanges: true,
        };
      });
      // Recalculate all tabs immediately so the comparison preview is up to date
      const tabIds = get().calculatorTabs.map(t => t.id);
      tabIds.forEach(id => get().calculateAmortizationSchedule(id));
    },

    loadSharedState: (shareState) => {
      const tabs: CalculatorTab[] = shareState.tabs.map((sharedTab, index) => ({
        id: sharedTab.id,
        title: sharedTab.title?.trim() ? sharedTab.title : `CALC ${index + 1}`,
        color: TAB_COLORS[index % TAB_COLORS.length],
        data: {
          mortgageTerms: sharedTab.mortgageTerms,
          annualExpenses: sharedTab.annualExpenses,
          extraPayments: sharedTab.extraPayments,
          amortizationSchedule: [],
          hasManualExtraPaymentEdits: !!(sharedTab.otpEdits && sharedTab.otpEdits.length > 0),
        },
      }));

      set({
        calculatorTabs: tabs,
        activeTabId: shareState.activeTabId,
        hasUnsavedChanges: false,
      });

      // Recalculate schedules for all tabs after state is set, then apply OTP edits
      const store = get();
      shareState.tabs.forEach(sharedTab => {
        store.calculateAmortizationSchedule(sharedTab.id);

        if (sharedTab.otpEdits && sharedTab.otpEdits.length > 0) {
          set(state => ({
            calculatorTabs: state.calculatorTabs.map(tab => {
              if (tab.id !== sharedTab.id) return tab;
              const schedule = tab.data.amortizationSchedule.map((entry, idx) => {
                const edit = sharedTab.otpEdits!.find(e => e.idx === idx);
                if (!edit) return entry;
                return {
                  ...entry,
                  oneTimePayment: edit.amount,
                  totalPayment: entry.payment + entry.expensePayment + edit.amount,
                };
              });
              return { ...tab, data: { ...tab.data, amortizationSchedule: schedule } };
            }),
          }));
          // Re-run with manual edits preserved so remaining balances and interest recompute (match post-OTP state).
          get().calculateAmortizationSchedule(sharedTab.id);
        }
      });
    },

    applyInitialGeoCountryToAllTabs: (country) => {
      set((state) => ({
        calculatorTabs: state.calculatorTabs.map((tab) => ({
          ...tab,
          data: {
            ...tab.data,
            mortgageTerms: mortgageTermsForCountryGeoSwitch(tab.data.mortgageTerms, country),
            amortizationSchedule: [],
          },
        })),
        hasUnsavedChanges: false,
      }));
      get().calculatorTabs.forEach((t) => get().calculateAmortizationSchedule(t.id));
    },

    calculateAmortizationSchedule: (tabId) => {
      const state = get();
      const tab = state.calculatorTabs.find(tab => tab.id === tabId);
      const tabData = tab?.data;

      if (!hasRequiredMortgageTerms(tabData?.mortgageTerms)) {
        return;
      }

      // Delegate all calculation to the pure engine function
      const scheduleInputs = {
        mortgageTerms: tabData.mortgageTerms,
        annualExpenses: tabData.annualExpenses,
        extraPayments: tabData.extraPayments,
      };
      const schedule = calculateSchedule(scheduleInputs);
      
      // Update the tab data with the new schedule
      set((state) => {
        const updatedTabs = state.calculatorTabs.map(tab => {
          if (tab.id === tabId) {
            const manualEdits = tab.data.hasManualExtraPaymentEdits
              ? tab.data.amortizationSchedule
                  .map((entry, index) => ({ idx: index, amount: entry.oneTimePayment ?? 0 }))
                  .filter((edit) => edit.amount > 0)
              : [];
            const updatedSchedule = applyManualOneTimePaymentEdits(
              schedule,
              scheduleInputs,
              manualEdits
            );

            return {
              ...tab,
              data: {
                ...tab.data,
                amortizationSchedule: updatedSchedule,
                hasManualExtraPaymentEdits: tab.data.hasManualExtraPaymentEdits
              }
            };
          }
          return tab;
        });

        return {
          ...state,
          calculatorTabs: updatedTabs
        };
      });
    }
  };
});